// Activity Tracker for Automatic Session Timeout
// Monitors user activity and automatically logs out after 15 minutes of inactivity

let activityTimer: NodeJS.Timeout | null = null;
let lastActivityTime = Date.now();
let isTrackingActivity = false;

// 15 minutes in milliseconds
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;

// Events that count as user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
  'focus'
];

// Function to handle user activity
const handleActivity = () => {
  lastActivityTime = Date.now();
  
  // Reset the inactivity timer
  if (activityTimer) {
    clearTimeout(activityTimer);
  }
  
  // Set a new timeout
  activityTimer = setTimeout(() => {
    
    // Dispatch custom event for inactivity logout
    const event = new CustomEvent('auth-timeout', { 
      detail: { 
        message: 'You have been automatically logged out due to 15 minutes of inactivity.',
        reason: 'inactivity'
      } 
    });
    window.dispatchEvent(event);
    
    // Clear auth state
    const authStore = (window as any).__authStore?.getState();
    if (authStore?.logout) {
      authStore.logout();
    }
    
    // Clear all auth-related storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    
    // Redirect to login page
    setTimeout(() => {
      window.location.href = '/login?reason=inactivity';
    }, 2000);
  }, INACTIVITY_TIMEOUT);
};

// Start activity tracking
export const startActivityTracking = () => {
  if (isTrackingActivity) {
    return;
  }
  isTrackingActivity = true;
  
  // Add event listeners for activity detection
  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, handleActivity, true);
  });
  
  // Initialize the timer
  handleActivity();
};

// Stop activity tracking
export const stopActivityTracking = () => {
  if (!isTrackingActivity) {
    return;
  }
  
  isTrackingActivity = false;
  
  // Remove event listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, handleActivity, true);
  });
  
  // Clear the timer
  if (activityTimer) {
    clearTimeout(activityTimer);
    activityTimer = null;
  }
};

// Get time since last activity
export const getTimeSinceLastActivity = (): number => {
  return Date.now() - lastActivityTime;
};

// Check if user is considered inactive
export const isUserInactive = (): boolean => {
  return getTimeSinceLastActivity() > INACTIVITY_TIMEOUT;
};

// Reset activity tracking (useful when user performs authenticated actions)
export const resetActivityTimer = () => {
  if (isTrackingActivity) {
    handleActivity();
  }
};

// Get remaining time before timeout
export const getTimeUntilTimeout = (): number => {
  const timeSinceActivity = getTimeSinceLastActivity();
  return Math.max(0, INACTIVITY_TIMEOUT - timeSinceActivity);
};